import type { ApolloError } from '@apollo/client';
import React from 'react';
import { useParams } from 'react-router';

import { Alert, Button } from 'src/shared/components';
import {
  PasswordField,
  Form as SharedForm,
  TextField,
} from 'src/shared/components/forms';
import { useLocation, useTranslate } from 'src/shared/hooks';
import { TranslationNamespace } from 'src/shared/hooks/useTranslate';
import { isNetworkError } from 'src/shared/utils/graphql';

import type { ResetPasswordFormData } from './types';
import { makeValidationSchema } from './validationSchema';

interface ResetPasswordFormProps {
  onSubmit: (formData: ResetPasswordFormData, token: string) => Promise<void>;
  loading: boolean;
  error: ApolloError | undefined;
  submitText: string;
}

const ResetPasswordForm = ({
  onSubmit,
  error,
  loading,
  submitText,
}: ResetPasswordFormProps) => {
  const t = useTranslate(TranslationNamespace.LOGIN);
  const { token } = useParams<{ token: string }>();

  const onFormSubmit = async ({ password }: ResetPasswordFormData) => {
    await onSubmit({ password }, token);
  };

  const { searchParams } = useLocation();
  const emailParam = searchParams.get('email');

  const initialValues = {
    email: emailParam,
    password: '',
  };

  const validationSchema = makeValidationSchema(t);

  const errorMessage = isNetworkError(error)
    ? t('somethingWentWrongWithYourConnection')
    : t('somethingWentWrongOnOurSide');

  return (
    <SharedForm<ResetPasswordFormData>
      initialValues={initialValues}
      onSubmit={onFormSubmit}
      validationSchema={validationSchema}
    >
      {emailParam && <TextField isDisabled label="Email" name="email" />}
      <PasswordField
        label="Password"
        name="password"
        placeholder="New Password"
      />
      {error && (
        <Alert
          alignSelf="stretch"
          color={error ? 'danger' : 'warning'}
          flexDirection="row"
        >
          {errorMessage}
        </Alert>
      )}
      <Button
        alignSelf="stretch"
        isLoading={loading}
        marginTop={500}
        size="large"
        type="submit"
      >
        {submitText}
      </Button>
    </SharedForm>
  );
};

export { ResetPasswordForm };
